
import Component from 'vue-class-component'
import { languages } from '@/types/languages'
import { Mixins, Prop, Watch } from 'vue-property-decorator'
import MixinInterface from '@/_mixins/index'
import ProjectMixin from '@/_mixins/project'
import { getRequest, postRequest, patchRequest, deleteRequest } from '@/plugins/api-axios'
import RiskLevelSelect from '@/views/Project/Form/components/riskLevelSelect.vue'
import { deserialise, serialise } from 'kitsu-core'
import FormResponse from '@/views/Project/Form/components/formResponse.vue'
import DialogTemplate from '@/_components/dialog/template/template.vue'
import FormStepContent from '@/views/Project/Form/components/formStepContent.vue'
import Draggable from 'vuedraggable'
import { openDialog } from '@/_components/dialog/dialog'
import ConfirmationDialog from '../dialogs/confirmation.vue'
import { Input } from '../viewModels/input'
import { Quiz } from '../viewModels/quiz'
import { YesNoInput } from '../viewModels/yesNoInput'
import { Step } from '../viewModels/step'
import ScoreLimit from './scoreLimit.vue'
import type { Text } from '@/views/Project/Form/types/form'

type AddOrUpdateRevisionParams = {
    isValid: boolean
    revision: any
    autoActivate: boolean
    keepOpen: boolean
}

@Component({
    mixins: [ProjectMixin],
    components: {
        DialogTemplate,
        Draggable,
        FormResponse,
        FormStepContent,
        RiskLevelSelect,
        ScoreLimit,
    },
})
export default class FormRevision extends Mixins<MixinInterface>() {
    @Prop() resourceId!: string
    @Prop() revisionId!: string
    @Prop() title!: string
    @Prop() supportScoring!: boolean
    @Prop({ default: false }) preActivationValidate!: boolean
    @Prop({ default: false }) previewBeforeCopying!: boolean

    isPreviewDialogOpen = false
    isCompareMode = false
    isPending = false
    isFetchingForm = false
    useCustomScoring = false
    hasUnsavedChanges = false
    form: any = null
    revision: any = null
    languages: { value: string; text: string }[] = []
    revisionTitle = this.textObject()
    formScores = [null, null] as [Maybe<number>, Maybe<number>]
    selectedLanguage = 'eng'
    comparedLanguage = 'eng'
    currentStep = 1
    steps: Array<Step> = []
    stepError = {}
    showStepError = false
    attachmentsToBeDeleted: string[] = []

    get stepLanguages() {
        if (this.isCompareMode) {
            return [this.selectedLanguage, this.comparedLanguage]
        }
        return [this.selectedLanguage]
    }

    get editorJSBlocks() {
        if (this.form.constraints?.attachments?.writable !== true) return []

        return [
            {
                type: 'VBITImage',
                config: {
                    type: 'formAttachment',
                    links: {
                        related: `${this.formBaseUrl}/formAttachments`,
                    },
                    relationships: {
                        form: {
                            data: {
                                type: 'form',
                                id: this.form?.id,
                            },
                        },
                    },
                },
            },
        ]
    }

    get isWritable() {
        return (
            !this.revision?.constraints?.data || this.revision?.constraints?.data?.writable === true
        )
    }

    get previewedRevision() {
        let revision = { ...this.revision }
        revision.data.steps = this.steps
        return revision
    }

    get step() {
        return this.steps[this.currentStep - 1] // zero based
    }

    get allLanguagesByCode() {
        return languages.reduce((acc, language) => {
            acc[language['639-2']] = language
            return acc
        }, {})
    }

    get availableLanguages() {
        return languages.map((language) => ({
            text: language['language-name'],
            value: language['639-2'],
        }))
    }

    get rules() {
        return {
            required: (value: string) => !!value || this.$t('fe_validation.error_required'),
        }
    }

    @Watch('isCompareMode')
    setComparedLanguage() {
        const differentLanguage = this.languages.find(
            (lang) => lang.value !== this.selectedLanguage
        )
        this.comparedLanguage = differentLanguage?.value || this.selectedLanguage
    }

    textObject(value: Maybe<string> = null): Text {
        return { [this.selectedLanguage]: value }
    }

    deleteStepQuiz(id: string) {
        if (!this.step?.quiz) return
        this.step.quiz = this.step.quiz.filter((quiz) => quiz._id !== id)
    }

    addNewStep() {
        const newStep = new Step(this.selectedLanguage, {
            scoreLimitMedium: this.useCustomScoring ? 4 : null,
            scoreLimitHigh: this.useCustomScoring ? 7 : null,
        })
        this.steps.push(newStep)
        this.$set(this.stepError, newStep._id, true)
        this.$nextTick(() => {
            this.currentStep = this.steps.length
        })
    }

    removeStep() {
        this.$delete(this.stepError, this.step._id)
        this.steps = this.steps.filter((_, index) => index !== this.currentStep - 1)
        this.$nextTick(() => {
            this.currentStep = this.steps.length
        })
    }

    addStepItem(
        ItemInstance: typeof Input | typeof Quiz,
        dependentId: string | null = null,
        inputId: string | null = null
    ) {
        const item = new ItemInstance(this.selectedLanguage)
        if (item instanceof Input) {
            if (this.supportScoring && item.supportScoring) {
                item.scoreLimitMedium = 4
                item.scoreLimitHigh = 7
            }

            item.dependsOn = dependentId
            if (!this.step.inputs) {
                this.$set(this.step, 'inputs', [])
            }

            if (dependentId) {
                // add dependent input after the input it depends on
                const inputIndex = this.step.inputs!.findIndex((input) => input.id === inputId)
                this.step.inputs?.splice(inputIndex + 1, 0, item)
            } else {
                // add new input to the end of the list
                // this.step.inputs?.push(item)
                this.step.inputs?.splice(this.step.inputs.length, 0, item)
            }
        } else {
            if (!this.step.quiz) {
                this.$set(this.step, 'quiz', [])
            }
            this.step.quiz?.push(item)
        }
    }

    newLanguageItem(code: string) {
        return {
            value: code,
            text: this.allLanguagesByCode[code]['language-name'] as string,
        }
    }

    async confirmUsingCustomScoring() {
        if (!this.isWritable) return

        const ok = await openDialog(ConfirmationDialog, {
            title: this.$t(`form_fe["dialog.use_advanced_scoring_title"]`),
            message: this.$t(`form_fe["dialog.use_advanced_scoring_message"]`),
            okButtonText: 'OK',
        })
        if (ok) {
            this.useCustomScoring = true
            this.formScores = [4, 7]
            this.steps.forEach((step) => {
                step.scoreLimitMedium = 4
                step.scoreLimitHigh = 7
                step.inputs?.forEach((input) => {
                    if (input.supportScoring) {
                        input.scoreLimitMedium = 4
                        input.scoreLimitHigh = 7
                    }
                })
            })
        }
    }

    handleLanguageChanged(language: string) {
        this.steps.forEach((step) => {
            step.inputs?.forEach((input) => {
                if (input instanceof YesNoInput) {
                    input.setItemDefaultText(language)
                }
            })
        })
    }

    checkTranslatableFields() {
        let deleteLanguageKey = (obj: any) => {
            if (!obj) return

            Object.keys(obj).forEach((key) => {
                if (!this.languages.find((lang) => lang.value === key)) {
                    this.$delete(obj, key)
                }
            })
        }

        deleteLanguageKey(this.revisionTitle)

        this.steps.forEach((step) => {
            deleteLanguageKey(step.title)
            deleteLanguageKey(step.text)

            step.inputs?.forEach((input) => {
                deleteLanguageKey(input.text)
                deleteLanguageKey(input.helpText)

                input.items?.forEach((inputItem) => {
                    deleteLanguageKey(inputItem.text)
                })
            })

            step.quiz?.forEach((quiz) => {
                deleteLanguageKey(quiz.text)

                quiz.answers.forEach((answer) => {
                    deleteLanguageKey(answer.text)
                })
            })
        })
    }

    handleLanguageListChanged(languages: string[]) {
        if (languages.length < 2) {
            this.isCompareMode = false
        }

        this.languages = languages.map(this.newLanguageItem)
        if (this.languages.length > 0) {
            const selectedLanguageInList = this.languages.find(
                (lang) => lang.value === this.selectedLanguage
            )
            if (!selectedLanguageInList) {
                this.selectedLanguage = this.languages[0].value
            }
            this.comparedLanguage =
                this.languages.find((lang) => lang.value !== this.selectedLanguage)?.value ||
                this.selectedLanguage
        }
        this.checkTranslatableFields()
        this.hasUnsavedChanges = true
    }

    async handleClose() {
        let discardChanges = true

        if (this.hasUnsavedChanges) {
            discardChanges = await this.promptUnsavedChanges()
        }

        if (discardChanges) {
            this.$emit('close')
        }
    }

    validateSteps(activateMode = false): {
        isValid: boolean
        stepIndex: number
        errorMessage?: string
    } {
        let isValid = false
        let stepIndex = 1
        let errorMessage

        for (; stepIndex <= this.steps.length; stepIndex++) {
            const step = this.steps[stepIndex - 1]
            let stepFormRefs = this.$refs[`stepForm${stepIndex - 1}`]

            // validate with built-in v-form validation
            if (Array.isArray(stepFormRefs)) {
                isValid = stepFormRefs.every((formRef: any) => formRef.validate())
            }

            // if step is still valid, continute validating inputs for invalid data language (only in activation context)
            if (isValid && activateMode) {
                let languageItem
                for (languageItem of this.languages) {
                    const language = languageItem.value

                    if (
                        (step.title && !step.title[language]) ||
                        (step.text && !step.text[language])
                    ) {
                        isValid = false
                        break
                    }

                    let stepInputs = step.inputs || []
                    let stepQuizes = step.quiz || []

                    for (let input of stepInputs) {
                        if (
                            (input.text && !input.text[language]) ||
                            (input.helpText && !input.helpText[language])
                        ) {
                            isValid = false
                            break
                        }

                        if (input.items) {
                            for (let item of input.items) {
                                if (item.text && !item.text[language]) {
                                    isValid = false
                                    break
                                }
                            }
                        }
                    }

                    if (!isValid) break

                    for (let quiz of stepQuizes) {
                        if (quiz.text && !quiz.text[language]) {
                            isValid = false
                            break
                        }

                        for (let answer of quiz.answers) {
                            if (answer.text && !answer.text[language]) {
                                isValid = false
                                break
                            }
                        }
                    }

                    if (!isValid) break
                }

                if (!isValid) {
                    errorMessage = this.$t(
                        `form_fe['revision.error.fieldsLanguageRequiredForActivation']`,
                        {
                            language: languageItem.text,
                        }
                    )
                    if (this.selectedLanguage !== languageItem.value) {
                        this.comparedLanguage = languageItem.value
                    }
                }
            }

            if (!isValid) break

            // Every time we validate a form, we need to update the stepError object in case
            // user filled out all the fields in the step, that had missing information before, not to confuse him
            this.stepError[step._id] = isValid = true
        }

        return { isValid, stepIndex, errorMessage }
    }

    isEmptyLanguageObject(languageObject: any) {
        if (!languageObject) return true

        let languageKeys = Object.keys(languageObject)
        for (let language of languageKeys) {
            if (!languageObject[language]) {
                this.$delete(languageObject, language)
            }
        }

        return Object.keys(languageObject).length === 0
    }

    validate(activateMode = false) {
        // revision title validation
        let isValid = (this.$refs.formEl as any).validate()

        let stepValidationResult
        if (isValid) {
            // continue validate step data
            stepValidationResult = this.validateSteps(activateMode)
            isValid = stepValidationResult.isValid
        }

        if (!isValid && activateMode) {
            this.currentStep = stepValidationResult.stepIndex
            this.stepError[this.steps[this.currentStep - 1]._id] =
                stepValidationResult?.errorMessage ||
                this.$t('form_fe["revision.stepFieldRequired_error"]')

            setTimeout(() => (this.showStepError = true), 500)
            setTimeout(() => (this.showStepError = false), 3000)
            this.$nextTick(() => {
                const stepEl = document.querySelector(`[data-step-index="${this.currentStep}"]`)
                if (stepEl) {
                    stepEl.classList.add('shake')
                    setTimeout(() => stepEl.classList.remove('shake'), 500)
                }
            })
        }

        return isValid
    }

    async deleteFormAttachments(ids: string[]) {
        if (!ids.length) return

        for (let fileId of ids) {
            const attachmentResponse = await getRequest(
                this.getURL({
                    path: `formAttachments/${fileId}`,
                    base: this.formBaseUrl,
                })
            )

            if (!attachmentResponse.data.data.attributes.canDelete) continue

            deleteRequest(
                this.getURL({
                    path: `formAttachments/${fileId}`,
                    base: this.formBaseUrl,
                }),
                {}
            )
        }
    }

    async addOrUpdate({ autoActivate = false, keepOpen = false }) {
        let isValid = this.validate(autoActivate)
        this.checkTranslatableFields()

        const revision = {
            languages: this.languages.map((lang) => lang.value),
            steps: this.steps.map((step) => {
                step.inputs?.forEach((input) => {
                    if (this.isEmptyLanguageObject(input.helpText)) input.helpText = null
                })
                if (this.isEmptyLanguageObject(step.text)) step.text = null
                if (this.isEmptyLanguageObject(step.title)) step.title = null
                return step
            }),
            title: this.revisionTitle,
            scoreLimitMedium: this.formScores[0] || null,
            scoreLimitHigh: this.formScores[1] || null,
        }

        this.deleteFormAttachments(this.attachmentsToBeDeleted)
        this.attachmentsToBeDeleted = []

        if (this.revision?.id) {
            this.updateRevision({ isValid, revision, autoActivate, keepOpen })
        } else {
            this.addRevision({ isValid, revision, autoActivate, keepOpen })
        }
    }

    async updateRevision({ isValid, revision, autoActivate, keepOpen }: AddOrUpdateRevisionParams) {
        if (!this.revision?.id) return

        try {
            this.isPending = true
            let payload = serialise('formRevision', { id: this.revision.id, data: revision })
            let response = await patchRequest(
                this.getURL({
                    path: `formRevisions/${this.revision.id}?include=form`,
                    base: this.formBaseUrl,
                }),
                {},
                payload.data
            )
            const updatedRevision = deserialise(response.data).data
            isValid = updatedRevision.isDataValid

            this.hasUnsavedChanges = false

            this.$emit('updated', {
                autoActivate: isValid ? autoActivate : false,
                revision: updatedRevision,
                resourceId: this.resourceId,
                keepOpen: !isValid && autoActivate ? true : keepOpen,
            })

            let snackbarText = ''

            if (isValid) {
                snackbarText = autoActivate
                    ? this.$t(
                          'form_fe["revision.snackbar.revision_saved_and_activated"]'
                      ).toString()
                    : this.$t('form_fe["revision.snackbar.revision_saved"]').toString()
            } else {
                snackbarText = autoActivate
                    ? this.$t(
                          'form_fe["revision.snackbar.revision_saved_and_invalid_for_activation"]'
                      ).toString()
                    : this.$t('form_fe["revision.snackbar.revision_saved"]').toString()
            }
            this.$store.commit('setSuccessSnackBar', {
                subtitle: snackbarText,
                timeout: 6000,
            })
            if (!isValid && autoActivate) {
                this.validateFormsInCompareMode()
            }
        } finally {
            this.isPending = false
        }
    }

    async addRevision({ isValid, revision, autoActivate, keepOpen }: AddOrUpdateRevisionParams) {
        if (!this.form?.id) return

        try {
            this.isPending = true
            let payload = serialise('formRevision', {
                data: revision,
                form: {
                    data: { type: 'form', id: this.form.id },
                },
            })
            let response = await postRequest(
                this.getURL({
                    path: 'formRevisions?include=form',
                    base: this.formBaseUrl,
                    params: { include: 'form' },
                }),
                {},
                payload.data
            )

            this.$emit('added', {
                autoActivate: isValid ? autoActivate : false,
                preActivationValidate: autoActivate,
                revision: deserialise(response.data).data,
                resourceId: this.resourceId,
                keepOpen: !isValid && autoActivate ? true : keepOpen,
            })
            let snackbarText = ''

            if (isValid) {
                snackbarText = autoActivate
                    ? this.$t(
                          'form_fe["revision.snackbar.revision_saved_and_activated"]'
                      ).toString()
                    : this.$t('form_fe["revision.snackbar.revision_saved"]').toString()
            } else {
                snackbarText = autoActivate
                    ? this.$t(
                          'form_fe["revision.snackbar.revision_saved_and_invalid_for_activation"]'
                      ).toString()
                    : this.$t('form_fe["revision.snackbar.revision_saved"]').toString()
            }

            this.$store.commit('setSuccessSnackBar', {
                subtitle: snackbarText,
                timeout: 6000,
            })
        } finally {
            this.isPending = false
        }
    }

    promptUnsavedChanges() {
        return openDialog(ConfirmationDialog, {
            title: this.$t('fe_contractors["dialog.unsaved_change_title"]'),
            message: this.$t('fe_contractors["dialog.leave_confirmation_message"]'),
            okButtonText: this.$t('fe_contractors["dialog.btn_discard"]'),
        }) as Promise<boolean>
    }

    watchChanges() {
        const markAsChanged = () => (this.hasUnsavedChanges = true)
        this.$watch(() => this.steps, markAsChanged, { deep: true })
        this.$watch(() => this.revisionTitle, markAsChanged, { deep: true })
    }

    async created() {
        this.isFetchingForm = true

        let formResponse = await getRequest(
            this.getURL({
                path: `forms`,
                params: {
                    'filter[resource]': this.resourceId,
                    'page[limit]': '100',
                    include: 'activeRevision,draftRevisions,inactiveRevisions',
                },
                base: this.formBaseUrl,
            })
        )

        this.form = deserialise(formResponse.data).data[0]

        let allRevisions = [
            ...(this.form.activeRevision.data ? [this.form.activeRevision.data] : []),
            ...this.form.draftRevisions.data.sort((a: any, b: any) => b.version - a.version),
            ...this.form.inactiveRevisions.data,
        ]

        if (this.revisionId) {
            this.revision = allRevisions.find((revision) => revision.id === this.revisionId)
        } else if (allRevisions[0]?.data) {
            this.revision = { data: allRevisions[0].data }
        }

        this.languages = this.revision?.data?.languages.map(this.newLanguageItem) || [
            this.newLanguageItem(this.currentUser.attributes.language),
        ]

        // Try and set user language as default
        this.setUserLanguageAsSelected()

        this.revisionTitle = this.revision?.data?.title || this.textObject(this.title)
        this.useCustomScoring = Boolean(
            this.revision?.data?.scoreLimitMedium && this.revision?.data?.scoreLimitHigh
        )
        this.formScores = [
            this.revision?.data?.scoreLimitMedium,
            this.revision?.data?.scoreLimitHigh,
        ]

        if (Array.isArray(this.revision?.data?.steps)) {
            this.revision.data.steps.forEach((stepData) => {
                const step = new Step(this.selectedLanguage, stepData)
                this.$set(this.stepError, step._id, true)
                this.steps.push(step)
            })
        } else {
            const step = new Step(this.selectedLanguage)
            this.$set(this.stepError, step._id, true)
            this.steps.push(step)
        }

        this.isFetchingForm = false

        if (this.preActivationValidate) {
            this.validateFormsInCompareMode()
        }

        this.watchChanges()
    }

    validateFormsInCompareMode() {
        this.isCompareMode = this.languages.length > 1
        this.$nextTick(() => this.validate(true))
    }

    setUserLanguageAsSelected() {
        // First trying to find exact match between user language and available languages
        let index = this.languages.findIndex((lang) => lang.value === this.userLanguage)

        // Handeling norwegian language
        if (index == -1) {
            if (this.userLanguage == 'nor' || this.userLanguage == 'nob') {
                index = this.languages.findIndex(
                    (lang) => lang.value === 'nor' || lang.value === 'nob'
                )
            }
        }
        if (index == -1) index = 0

        // Compared language should be different from selected language
        let comparedLanguageIndex = this.languages.findIndex(
            (lang) => lang.value !== this.languages[index].value
        )

        this.selectedLanguage = this.languages[index].value
        this.comparedLanguage =
            this.languages[comparedLanguageIndex]?.value || this.selectedLanguage
    }

    closeLanguageMenu() {
        this.$refs.languageAutocomplete?.blur()
    }
}
